// SPDX-License-Identifier: MIT
// RetryHandler - exponential backoff with jitter (Phase 5)

import { ClassifiedError, ErrorCategory } from '../download/types';

export interface RetryPolicyOptions {
  maxAttempts?: number; // inclusive fallback, default 3
  baseDelayMs?: number; // default 300
  maxDelayMs?: number; // cap, default 4000
  multiplier?: number; // default 2
  jitter?: boolean; // default true
  /** Optional category specific attempt overrides (e.g. throttle/network more generous). */
  categoryAttempts?: Partial<Record<ErrorCategory, number>>;
  /** Optional category specific base delays (e.g. throttle start higher). */
  categoryBaseDelayMs?: Partial<Record<ErrorCategory, number>>;
}

export class RetryHandler {
  private opts: Required<Omit<RetryPolicyOptions, 'categoryAttempts' | 'categoryBaseDelayMs'>> &
    Pick<RetryPolicyOptions, 'categoryAttempts' | 'categoryBaseDelayMs'>;
  constructor(opts: RetryPolicyOptions = {}) {
    this.opts = {
      maxAttempts: opts.maxAttempts ?? 3,
      baseDelayMs: opts.baseDelayMs ?? 300,
      maxDelayMs: opts.maxDelayMs ?? 4000,
      multiplier: opts.multiplier ?? 2,
      jitter: opts.jitter ?? true,
      categoryAttempts: opts.categoryAttempts,
      categoryBaseDelayMs: opts.categoryBaseDelayMs,
    };
  }

  shouldRetry(err: ClassifiedError, attempt: number): boolean {
    if (!err.retryable) return false;
    const cap = this.opts.categoryAttempts?.[err.category] ?? this.opts.maxAttempts;
    return attempt < cap;
  }

  /** Compute delay for given attempt (1-based attempt index). */
  delayFor(attempt: number, category?: ErrorCategory): number {
    const base = (category && this.opts.categoryBaseDelayMs?.[category]) ?? this.opts.baseDelayMs;
    const expo = base * Math.pow(this.opts.multiplier, attempt - 1);
    const capped = Math.min(expo, this.opts.maxDelayMs);
    if (!this.opts.jitter) return capped;
    const jitterPortion = capped * 0.3; // 30% jitter
    return Math.round(capped - jitterPortion / 2 + Math.random() * jitterPortion);
  }

  async waitBeforeRetry(
    attempt: number,
    category?: ErrorCategory,
    signal?: AbortSignal
  ): Promise<void> {
    const ms = this.delayFor(attempt, category);
    await new Promise<void>((resolve, reject) => {
      const to = setTimeout(() => resolve(), ms);
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(to);
          reject(new Error('cancelled'));
        });
      }
    });
  }
}
